import { useCallback, useEffect, useMemo, useState } from "react";
import { Task, TaskStatus } from "@common/types/task";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { useUpdateTask } from "@api/task.api";
import { useRunTaxonomyExtractionMutation } from "@api/financialDocument.api";
import { useDocumentRetrievalExclusion } from "./useDocumentRetrievalExclusion";
import { useDocumentReviewDrawer } from "./useDocumentReviewDrawer";
import { useExpectedAccountNumberCandidates } from "./useExpectedAccountNumberCandidates";
import {
    generateMonthlySeeds,
    matchDocumentsToMonths,
} from "./documentRetrievalHelpers";
import { getNextOptimisticExpiryMs } from "./documentVerificationStatus";

interface UseDocumentRetrievalTaskModelParams {
    selectedTask: Task;
    detachDocuments: (documentIds: number[]) => Promise<boolean>;
}

export function useDocumentRetrievalTaskModel({
    selectedTask,
    detachDocuments,
}: UseDocumentRetrievalTaskModelParams) {
    const { isViewOnly } = useViewOnlyRefusal();
    const markCompleteTooltip = useViewOnlyTooltipReason("");
    const [unverifiedOpen, setUnverifiedOpen] = useState(true);
    const [showCompleteConfirmation, setShowCompleteConfirmation] =
        useState(false);

    const updateTask = useUpdateTask(selectedTask);
    const {
        excludedPeriods,
        handleExclude,
        handleExcludeMany,
        handleClearExclusion,
    } = useDocumentRetrievalExclusion(selectedTask);
    const {
        mutate: retryExtraction,
        isPending: isRetrying,
        variables: retryingDocumentId,
    } = useRunTaxonomyExtractionMutation();

    const handleRetry = useCallback(
        (documentId: number) => {
            retryExtraction(documentId);
        },
        [retryExtraction],
    );

    const isCompleted = selectedTask.status === TaskStatus.DONE;

    const handleMarkComplete = useCallback(async () => {
        try {
            await updateTask.mutateAsync({ status: TaskStatus.DONE });
            setShowCompleteConfirmation(false);
        } catch {
            // modal stays open so user can retry
        }
    }, [updateTask]);

    const handleMarkIncomplete = useCallback(async () => {
        try {
            await updateTask.mutateAsync({ status: TaskStatus.TODO });
        } catch {
            // mutation error state is the recovery path
        }
    }, [updateTask]);

    const viewFilters = selectedTask.view?.filters;
    const periodStart = viewFilters?.periodStart ?? "";
    const periodEnd = viewFilters?.periodEnd ?? "";
    const expectedCategoryIds = viewFilters?.documentCategoryIds;

    const {
        candidates: expectedAccountNumberCandidates,
        account: expectedAccount,
        isLoading: expectedAccountNumberCandidatesLoading,
    } = useExpectedAccountNumberCandidates(selectedTask);

    const serverDocs = useMemo(
        () => selectedTask.financialDocuments ?? [],
        [selectedTask.financialDocuments],
    );

    const [periodOverride, setPeriodOverride] = useState<{
        docId: number;
        periodStartDate?: string | null;
        periodEndDate?: string | null;
    } | null>(null);

    useEffect(
        function clearPeriodOverrideWhenServerCatchesUp() {
            if (!periodOverride) {
                return;
            }
            const serverDoc = serverDocs.find(
                (doc) => doc.id === periodOverride.docId,
            );
            if (!serverDoc) {
                setPeriodOverride(null);
                return;
            }
            const extraction = serverDoc.taxonomyExtraction;
            const isStartCaughtUp =
                periodOverride.periodStartDate === undefined ||
                extraction?.periodStartDate === periodOverride.periodStartDate;
            const isEndCaughtUp =
                periodOverride.periodEndDate === undefined ||
                extraction?.periodEndDate === periodOverride.periodEndDate;
            if (isStartCaughtUp && isEndCaughtUp) {
                setPeriodOverride(null);
            }
        },
        [serverDocs, periodOverride],
    );

    const handlePeriodStartDateOverride = useCallback(
        (docId: number, value: string | null) => {
            if (isViewOnly) {
                return;
            }
            setPeriodOverride((prev) => ({
                ...(prev?.docId === docId ? prev : {}),
                docId,
                periodStartDate: value,
            }));
        },
        [isViewOnly],
    );

    const handlePeriodEndDateOverride = useCallback(
        (docId: number, value: string | null) => {
            if (isViewOnly) {
                return;
            }
            setPeriodOverride((prev) => ({
                ...(prev?.docId === docId ? prev : {}),
                docId,
                periodEndDate: value,
            }));
        },
        [isViewOnly],
    );

    const rawDocs = useMemo(() => {
        if (!periodOverride) {
            return serverDocs;
        }
        return serverDocs.map((doc) => {
            if (doc.id !== periodOverride.docId) {
                return doc;
            }
            const base = doc.taxonomyExtraction ?? {
                periodStartDate: null,
                periodEndDate: null,
                accountNumber: null,
                institutionName: null,
                otherAccountIdentifiers: null,
            };
            return {
                ...doc,
                taxonomyExtraction: {
                    ...base,
                    ...(periodOverride.periodStartDate !== undefined && {
                        periodStartDate: periodOverride.periodStartDate,
                    }),
                    ...(periodOverride.periodEndDate !== undefined && {
                        periodEndDate: periodOverride.periodEndDate,
                    }),
                },
            };
        });
    }, [serverDocs, periodOverride]);

    const [now, setNow] = useState(() => new Date());
    useEffect(
        function scheduleOptimisticVerificationExpiry() {
            const delay = getNextOptimisticExpiryMs(rawDocs, new Date());
            if (delay === null) {
                return undefined;
            }
            const handle = setTimeout(() => setNow(new Date()), delay);
            return () => clearTimeout(handle);
        },
        [rawDocs, now],
    );

    const { rows, unverifiedDocs } = useMemo(() => {
        const months = generateMonthlySeeds(periodStart, periodEnd);
        return matchDocumentsToMonths({
            months,
            docs: rawDocs,
            excludedPeriods,
            expectedCategoryIds,
            expectedAccountNumberCandidates,
            expectedAccountNumberCandidatesLoading,
            now,
        });
    }, [
        periodStart,
        periodEnd,
        rawDocs,
        excludedPeriods,
        expectedCategoryIds,
        expectedAccountNumberCandidates,
        expectedAccountNumberCandidatesLoading,
        now,
    ]);

    const monthKeys = useMemo(() => new Set(rows.map((r) => r.key)), [rows]);

    const occupiedMonths = useMemo(() => {
        const documentsByMonth = new Map<string, number>();
        for (const row of rows) {
            if (row.documentId) {
                documentsByMonth.set(row.key, row.documentId);
            }
        }
        return documentsByMonth;
    }, [rows]);

    const {
        previewedDoc,
        closePreview: closeDrawer,
        openPreview,
        handleRowClick,
        handleUnverifiedRowClick,
    } = useDocumentReviewDrawer({ rawDocs, rows, unverifiedDocs });

    const closePreview = useCallback(() => {
        setPeriodOverride(null);
        closeDrawer();
    }, [closeDrawer]);

    const [removingDocumentIds, setRemovingDocumentIds] = useState<number[]>(
        [],
    );
    const handleRemoveDocumentsFromTask = useCallback(
        async (documentIds: number[]) => {
            if (isViewOnly || documentIds.length === 0) {
                return false;
            }
            setRemovingDocumentIds(documentIds);
            const didSucceed = await detachDocuments(documentIds);
            setRemovingDocumentIds([]);
            if (
                didSucceed &&
                previewedDoc != null &&
                documentIds.includes(previewedDoc.id)
            ) {
                closePreview();
            }
            return didSucceed;
        },
        [detachDocuments, closePreview, previewedDoc, isViewOnly],
    );
    const handleRemoveFromTask = useCallback(
        async (documentId: number) => {
            await handleRemoveDocumentsFromTask([documentId]);
        },
        [handleRemoveDocumentsFromTask],
    );

    const matchedCount = rows.filter((r) => r.status === "verified").length;
    const completeCount = rows.filter(
        (r) => r.status === "verified" || r.status === "excluded",
    ).length;
    const totalCount = rows.length;
    const isAllResolved = completeCount >= totalCount && totalCount > 0;

    return {
        isViewOnly,
        markCompleteTooltip,
        unverifiedOpen,
        setUnverifiedOpen,
        showCompleteConfirmation,
        setShowCompleteConfirmation,
        handleExclude,
        handleExcludeMany,
        handleClearExclusion,
        handleRetry,
        isRetrying,
        retryingDocumentId,
        isCompleted,
        handleMarkComplete,
        handleMarkIncomplete,
        expectedCategoryIds,
        expectedAccount,
        expectedAccountNumberCandidates,
        expectedAccountNumberCandidatesLoading,
        rawDocs,
        handlePeriodStartDateOverride,
        handlePeriodEndDateOverride,
        rows,
        unverifiedDocs,
        monthKeys,
        occupiedMonths,
        previewedDoc,
        closePreview,
        openPreview,
        handleRowClick,
        handleUnverifiedRowClick,
        handleRemoveFromTask,
        handleRemoveDocumentsFromTask,
        removingDocumentIds,
        matchedCount,
        totalCount,
        isAllResolved,
    };
}
